import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { ClerkProvider, SignedIn, SignedOut } from "@clerk/clerk-react";
import { Toaster } from "@/components/ui/sonner";

import Landing from "@/pages/Landing";
import SignInPage from "@/pages/SignInPage";
import SignUpPage from "@/pages/SignUpPage";
import Onboarding from "@/pages/Onboarding";
import Profile from "@/pages/Profile";
import Dashboard from "@/pages/Dashboard";
import DashboardTopic from "@/pages/DashboardTopic";
import BuildScreen from "@/pages/BuildScreen";
import AdminShell from "@/pages/admin/AdminShell";
import AdminCreatorsList from "@/pages/admin/AdminCreatorsList";
import AdminCreatorDetail from "@/pages/admin/AdminCreatorDetail";
import AdminCreatorView from "@/pages/admin/AdminCreatorView";
import AdminWork from "@/pages/admin/AdminWork";
import AdminGuides from "@/pages/admin/AdminGuides";
import AdminTracking from "@/pages/admin/AdminTracking";
import AdminTools from "@/pages/admin/AdminTools";
import NotFound from "@/pages/NotFound";
import PageHead from "@/components/PageHead";

const PUBLISHABLE_KEY = process.env.REACT_APP_CLERK_PUBLISHABLE_KEY;

const samoonyAppearance = {
    variables: {
        colorPrimary: "#111110",
        colorText: "#111110",
        colorBackground: "#f6f4ef",
        colorInputBackground: "#ffffff",
        colorInputText: "#111110",
        colorDanger: "#ff2e7e",
        borderRadius: "8px",
        fontFamily: "Archivo, system-ui, sans-serif",
    },
    elements: {
        card: "shadow-none border border-black/10 rounded-2xl bg-white",
        headerTitle: "font-display text-2xl tracking-tight",
        headerSubtitle: "text-black/60",
        formButtonPrimary:
            "samoony-pill samoony-pill-primary w-full !h-[52px] !rounded-full",
        formFieldInput: "samoony-input !h-[56px]",
        socialButtonsBlockButton: "!rounded-full !h-[52px]",
        footer: "hidden",
    },
};

function RequireAuth({ children }) {
    return (
        <>
            <SignedIn>{children}</SignedIn>
            <SignedOut>
                <Navigate to="/sign-in" replace />
            </SignedOut>
        </>
    );
}

function MissingKeyNotice() {
    return (
        <div className="min-h-screen flex items-center justify-center p-6">
            <div
                data-testid="missing-clerk-key"
                className="max-w-md border border-black/10 rounded-2xl p-6 bg-white"
            >
                <div className="samoony-label mb-3">Setup needed</div>
                <h1 className="font-display text-2xl tracking-tight mb-2">
                    Clerk key is not set.
                </h1>
                <p className="text-sm text-black/60">
                    Add REACT_APP_CLERK_PUBLISHABLE_KEY to frontend/.env and
                    CLERK_PUBLISHABLE_KEY plus CLERK_SECRET_KEY to backend/.env,
                    then restart the servers.
                </p>
            </div>
        </div>
    );
}

function App() {
    if (!PUBLISHABLE_KEY) {
        return <MissingKeyNotice />;
    }

    return (
        <HelmetProvider>
        <BrowserRouter>
            <ClerkProvider
                publishableKey={PUBLISHABLE_KEY}
                appearance={samoonyAppearance}
                signInUrl="/sign-in"
                signUpUrl="/sign-up"
                signInFallbackRedirectUrl="/onboarding"
                signUpFallbackRedirectUrl="/onboarding"
                afterSignOutUrl="/"
            >
                <Routes>
                    <Route
                        path="/"
                        element={
                            <>
                                <PageHead
                                    title="Samoony: local creator pages that AI assistants can name"
                                    description="Samoony builds public pages and city guides for local creators, so when people ask AI who to follow or book in their city, they get named."
                                    canonicalPath="/"
                                />
                                <Landing />
                            </>
                        }
                    />
                    <Route
                        path="/sign-in/*"
                        element={
                            <>
                                <PageHead
                                    title="Sign in | Samoony"
                                    noindex
                                />
                                <SignInPage />
                            </>
                        }
                    />
                    <Route
                        path="/sign-up/*"
                        element={
                            <>
                                <PageHead
                                    title="Sign up | Samoony"
                                    noindex
                                />
                                <SignUpPage />
                            </>
                        }
                    />
                    <Route
                        path="/onboarding"
                        element={
                            <RequireAuth>
                                <PageHead
                                    title="Onboarding | Samoony"
                                    noindex
                                />
                                <Onboarding />
                            </RequireAuth>
                        }
                    />
                    <Route
                        path="/profile"
                        element={
                            <RequireAuth>
                                <PageHead
                                    title="Account | Samoony"
                                    noindex
                                />
                                <Profile />
                            </RequireAuth>
                        }
                    />
                    <Route
                        path="/account"
                        element={
                            <RequireAuth>
                                <PageHead
                                    title="Account | Samoony"
                                    noindex
                                />
                                <Profile />
                            </RequireAuth>
                        }
                    />
                    <Route
                        path="/dashboard"
                        element={
                            <RequireAuth>
                                <PageHead
                                    title="Dashboard | Samoony"
                                    noindex
                                />
                                <Dashboard />
                            </RequireAuth>
                        }
                    />
                    <Route
                        path="/dashboard/topic/:slug"
                        element={
                            <RequireAuth>
                                <PageHead
                                    title="Dashboard topic | Samoony"
                                    noindex
                                />
                                <DashboardTopic />
                            </RequireAuth>
                        }
                    />
                    <Route
                        path="/build"
                        element={
                            <RequireAuth>
                                <PageHead
                                    title="Build | Samoony"
                                    noindex
                                />
                                <BuildScreen />
                            </RequireAuth>
                        }
                    />
                    <Route
                        path="/admin"
                        element={
                            <RequireAuth>
                                <AdminShell />
                            </RequireAuth>
                        }
                    >
                        <Route index element={<Navigate to="/admin/creators" replace />} />
                        <Route path="creators" element={<AdminCreatorsList />} />
                        <Route path="creators/:id" element={<AdminCreatorDetail />} />
                        <Route path="creators/:id/view" element={<AdminCreatorView />} />
                        <Route path="work" element={<AdminWork />} />
                        <Route path="queue" element={<Navigate to="/admin/work" replace />} />
                        <Route path="guides" element={<AdminGuides />} />
                        <Route path="tracking" element={<AdminTracking />} />
                        <Route path="tools" element={<AdminTools />} />
                    </Route>
                    <Route path="*" element={<NotFound />} />
                </Routes>
                <Toaster position="top-center" />
            </ClerkProvider>
        </BrowserRouter>
        </HelmetProvider>
    );
}

export default App;
